import {
    BaseType,
    BuildComponentType,
    CardinalDirection, ColorEnum, ElevatorType, HazardousState, HeritageProtectionLevel, LevelType,
    Material, RiskConstructionType,
    RoofShape,
    RoomType,
    UtilityCategory,
    WindowPaneType
} from "./Enums";
import {BuildingType} from "./BuildingTypeEnums";
import {ComponentType} from "react";
import {AnyGeometryShape} from "./GeometryShapes";
import {DocumentModel} from "../Pages/Documents/DocumentsPage";
import {translate} from "./EnumTranslations";

export interface Building {
    key: string;
    summary: BuildingSummary;
    model: BuildingModel;
}

export interface BuildingSummary {
    key: string;
    buildingType?: BuildingType;
    name?: string;
    coordinates?: CoordinatesModel;
    locationCategories?: LocationCategories;
    currentCoverPhotoKey?: string;
    coverPhoto?: CoverPhotoModel;
    coverPhotos?: { [key: string]: PhotoModel };
    groupTags?: { [key: string]: GroupTag };
    buildingInfo?: BuildingInfo;
    responsibleGroups?: string[];
}

export interface BuildingModel {
    key: string;
    structure?: BuildingStructure;
    documents?: { [key: string]: DocumentModel };
    contactKeys?: string[];
    currentMapImageKey?: string;
    mapImages?: { [key: string]: PhotoModel };
    mapImage?: PhotoModel;
}

export interface BuildingStructure {
    components?: { [key: string]: BaseComponent};
    rooms?: { [key: string]: RoomModel };
    levels?: { [key: string]: LevelModel };
    types?: ComponentTypes;
}

export interface ComponentTypes {
    surfaceTypes: { [key: string]: SurfaceType };
    windowTypes: { [key: string]: WindowType };
    doorTypes: { [key: string]: DoorType };
}

export interface SurfaceType {
    key: string;
    interiorType: ComponentType;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
    label?: string;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    hazardous?: ValueWithMetadata<HazardousModel>;
}

export interface ModularSize {
    width: number;  // in meters
    height: number; // in meters
}

export interface WindowType {
    key: string;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
    paneType: ValueWithMetadata<WindowPaneType>;
    areaShapes: ValueWithMetadata<AnyGeometryShape>;
    modularSize?: ValueWithMetadata<ModularSize>;
    label?: string;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    hazardous?: ValueWithMetadata<HazardousModel>;
}

export interface DoorType {
    key: string;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
    areaShapes: ValueWithMetadata<AnyGeometryShape>;
    doorCategory: ValueWithMetadata<DoorCategory>;
    modularSize?: ValueWithMetadata<ModularSize>;
    label?: string;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    hazardous?: ValueWithMetadata<HazardousModel>;
}

export enum DoorCategory {
    interior_door = "interior_door",
    exterior_door = "exterior_door"
}

export enum ValueStatus {
    notSet = "notSet",
    inaccessible = "inaccessible",
    set = "set"
}

export interface ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;
}

export interface GroupTag {
    key: string;
    tag: string;
}

export interface canHavePhotos {
    photos?: { [key: string]: PhotoModel };
}

export interface canHaveLabel {
    label?: string;
}

export interface canHaveCondition {
    condition?: number;
}

export interface canHaveComponentType {
    specificType?: BuildComponentType;
}

export interface BaseComponent {

    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: BuildComponentType;
    condition?: ValueWithMetadata<number>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<Material[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    label?: string;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    canHaveLock?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    roomKey?: string;
    typeKey?: ValueWithMetadata<string>;
    parentSurface1?: ValueWithMetadata<string>;
    parentSurface2?: ValueWithMetadata<string>;
    parentSurface?: ValueWithMetadata<string>;
    blueprintLocations?: { [key: string]: BlueprintLocation };
    //lockMechanism?: ValueWithMetadata<LockMechanismModel>;
    utilityCategory?: UtilityCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;
    mapLocations?: { [key: string]: BlueprintLocation };
    installYear?: ValueWithMetadata<number>;
    photoLocation?: PhotoLocation;

    displayName(building: Building): string;
    totalArea(building: Building): number|null;
}

/**
 * Finds and returns the translation of the first toilet component found in the building model
 * Only applies to toiletReserve and toiletArchipelago building types
 */
export function getToiletComponentTranslation(
    summary: BuildingSummary,
    model: BuildingModel
): string | null {
    // Check if building type is one of the toilet types
    if (
        summary.buildingType !== BuildingType.toiletReserve &&
        summary.buildingType !== BuildingType.toiletArchipelago
    ) {
        return null;
    }

    // Check if model has components
    if (!model.structure?.components) {
        return null;
    }

    // Define the toilet component types we're looking for
    const toiletComponentTypes = [
        BuildComponentType.UtilityToiletWater,
        BuildComponentType.UtilityToiletClosed,
        BuildComponentType.UtilityToiletSolar,
        BuildComponentType.UtilityToiletCompost,
        BuildComponentType.UtilityToiletWasteBarrels
    ];

    // Iterate through all components to find the first toilet type
    for (const component of Object.values(model.structure.components)) {
        if (component.specificType && toiletComponentTypes.includes(component.specificType)) {
            return translate(component.specificType);
        }
    }

    return null;
}

export interface IsRoom extends canHaveLabel, canHavePhotos {
    key: string;
    number?: string;
    levelKey?: string;
    blueprintLocations?: { [key: string]: BlueprintLocation };
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
    electricalInstallation?: ValueWithMetadata<ElectricalInstallation>;
    airTerminalInstallation?: ValueWithMetadata<AirTerminalInstallation>;
    radiatorInstallation?: ValueWithMetadata<RadiatorInstallation>;
}

export interface FileModel {
    fileType?: string;
    key: string;
    name: string;
    uploadedAt?: string;
    url?: string;
}

export interface BuildingWithCoverUrl extends BuildingModel {
    coverPhotoUrl?: string;
}

export interface ColorModel {
    colorEnum?: ColorEnum;
    code?: string;
}
export interface PhotoModel {
    key: string;
    author: string;
    date: string;
    width: number;
    height: number;
    storagePath: string;
    fileSize?: number;
}

export interface PhotoUrlData {
    fullSizeUrl: string;
    mediumSizeUrl: string;
    smallSizeUrl: string;
    thumbnailsGenerated: boolean;
    updatedAt: number;
}

export interface CoverPhotoModel extends Record<string, PhotoUrlData | string | number> {
    author: string;
    date: string;
    height: number;
    key: string;
    storagePath: string;
    width: number;
}

export interface HazardousModel {
    asbestos?: HazardousState;
    pcb?: HazardousState;
    led?: HazardousState;
    arsenic?: HazardousState;
    blueConcrete?: HazardousState;
    radon?: HazardousState;
}

export interface ElectricalInstallation {
    lightSwitchCount?: number;
    lampCount?: number;
    powerOutletCount?: number;
    lightSwitchPhotos?: Record<string, PhotoModel>;
    lampPhotos?: Record<string, PhotoModel>;
    powerOutletPhotos?: Record<string, PhotoModel>;
}

export interface AirTerminalInstallation {
    exhaustCount?: number;
    supplyCount?: number;
}

export interface RadiatorInstallation {
    waterRadiatorCount?: number;
    electricRadiatorCount?: number;
}

export interface CoordinatesModel {
    latitude: number;
    longitude: number;
}

export interface BlueprintLocation {
    key: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface PhotoLocation {
    parentKey: string;
    storagePath: string;
    width: number;
    height: number;
    x: number;
    y: number;
}

export interface RoomModel extends IsRoom, canHaveLabel, canHavePhotos {}

export interface LevelModel {
    key: string;
    name?: string;
    number: number;
    ceilingHeight?: number;
    type?: LevelType;
    planImage?: PhotoModel;
    currentBlueprintKey?: string;
    blueprints?: { [key: string]: PhotoModel };
    roofPlan?: boolean; // true if this level is the roof plan (Takplan)
}

export interface RiskConstruction {
    risks?: RiskConstructionType[];
    customRisk?: string;
}

export interface LocationCategories {
    category1?: LocationCategory;
    category2?: LocationCategory;
    category3?: LocationCategory;
    category4?: LocationCategory;
    category5?: LocationCategory;
}

export interface LocationCategory {
    name: string;
    key: String;
}

export interface BuildingInfo {
    keyInstructions?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    other?: string;
    hasPoorReception?: boolean;
    seasonInfo?: SeasonInfo;
    heritageProtectionLevel?: HeritageProtectionLevel[];
    yearBuilt?: number;
}

export interface SeasonInfo {
    comment?: string;
}

export interface ContactModel {
    key: string;
    name: string;
    role?: string;
    company?: string;
    phoneNumber?: string;
    email?: string;
    info?: string;
}
